import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import GaugeChart from 'react-gauge-chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { connect, useSelector } from 'react-redux';
import { getSentimentAnalysis } from '../../actions/market-intelligence-action';
import moment from 'moment';
import { isProMasterYearly } from '../../utils/SubUtils';
import ReactMarkdown from 'react-markdown';

import { Spinner } from 'react-bootstrap';
import TutorialPopup from '../help/TutorialPopup';

const showMarketSentimentAndBuzz = false;

const SentimentAnalysis = ({ pairId, getSentimentAnalysis }) => {
  const { t } = useTranslation();
  const [selectedRange, setSelectedRange] = useState('30d');
  const [loading, setLoading] = useState(false);
  const sentimentData = useSelector((state) => state.marketIntelligence.sentimentAnalysis) || [];
  const assessment = useSelector((state) => state.marketIntelligence.sentimentAssessment) || "";
  const cleanAssessment = assessment && assessment.replace(/\\n/g, '\n');
  const subscription = useSelector((state) => state.auth.user?.user_subscription);
  const hasAccess = isProMasterYearly(subscription);

  useEffect(() => {
    if (pairId && hasAccess) {
        setLoading(true);
        getSentimentAnalysis(pairId, 90).finally(() => setLoading(false));
    }
  }, [pairId, getSentimentAnalysis, hasAccess]);

  // Filter data based on selected range
  const filteredData = useMemo(() => {
      if (!sentimentData.length) return [];
      const now = moment();
      let cutoffDate;
      if (selectedRange === '24h') {
          cutoffDate = now.clone().subtract(1, 'days').startOf('day');
      } else {
          const d = parseInt(selectedRange.replace('d', ''));
          cutoffDate = now.clone().subtract(d, 'days').startOf('day');
      }
      
      return sentimentData.filter(item => moment(item.date).isSameOrAfter(cutoffDate));
  }, [sentimentData, selectedRange]);

  // Calculate Averages
  const averages = useMemo(() => {
      if (!filteredData.length) return null;
      
      const sum = (key) => filteredData.reduce((acc, curr) => acc + (curr[key] || 0), 0);
      const count = filteredData.length;

      return {
          sentiment: sum('sentiment') / count,
          fear: sum('fear') / count,
          marketRisk: sum('marketRisk') / count,
          accountingSentiment: sum('accountingSentiment') / count, 
          trust: sum('trust') / count,
          innovation: sum('innovation') / count,
      };
  }, [filteredData]);

  // Metrics for Mini Gauges
  const metrics = averages ? [
    { label: 'Fear index', value: averages.fear, min: 0, max: 1, reverseColors: true, decimals: 4 },
    { label: 'Market Risk', value: averages.marketRisk, min: -1, max: 1 },
    { label: 'Fundamental', value: averages.accountingSentiment, min: -1, max: 1 }, 
    { label: 'Trust Score', value: averages.trust, min: -1, max: 1 },
    { label: 'Innovation', value: averages.innovation, min: 0, max: 0.1, decimals: 4 },
  ] : [];

  // Media Buzz Data
  const buzzData = useMemo(() => {
      const sorted = [...filteredData].sort((a, b) => new Date(a.date) - new Date(b.date));
      return sorted.map(item => ({
          name: moment(item.date).format('MMM D'),
          value: item.buzz
      })); 
  }, [filteredData]);

  // Reusable component for the Gauge
  const StockGauge = ({ value, label, size = "small", min = 0, max = 1, reverseColors = false, decimals = 2 }) => {
    const isLarge = size === "large";
    const safeValue = isNaN(value) ? 0 : value;
    
    // Normalize value to 0-1 range for the gauge
    // (val - min) / (max - min)
    const normalizedValue = Math.min(Math.max((safeValue - min) / (max - min), 0), 1);
    
    const colors = reverseColors 
      ? ['#52c41a', '#bae637', '#ffec3d', '#ff4d4f'] 
      : ['#ff4d4f', '#ffec3d', '#bae637', '#52c41a'];

    return (
      <div className={`text-center ${isLarge ? 'py-4' : 'p-2'}`}>
        <div style={{ width: isLarge ? '280px' : '100%', margin: '0 auto' }}>
          <GaugeChart
            id={`gauge-${label.replace(/\s+/g, '-').toLowerCase()}`}
            nrOfLevels={4}
            colors={colors}
            arcWidth={0.15}
            percent={normalizedValue} 
            textColor="transparent" 
            needleColor="var(--gauge-needle-color)"
            needleBaseColor="var(--gauge-needle-color)"
            hideText={true}
            animate={true}
          />
        </div>
        <div className="mt-n2">
          <div className={`${isLarge ? 'h4' : 'h6'} mb-0 fw-medium text-dark`}>{safeValue.toFixed(decimals)}</div>
          <div className="text-muted extra-small">{label}</div>
        </div>
      </div>
    );
  };

  if (!hasAccess) {
    return (
        <div className="stock-dashboard-wrapper mt-4 px-3">
             <div className="border-0 shadow-sm rounded-4 p-5 mb-4 text-center">
                <div className="py-5">
                    <div className="mb-3">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <h5 className="mb-3 fw-bold text-dark">Intrinsic Intelligence Locked</h5>
                    <p className="text-muted mb-4" style={{maxWidth: '400px', margin: '0 auto'}}>
                        Upgrade to <strong>Pro or Master Yearly</strong> plan to unlock advanced intrinsic intelligence data, including sentiment analysis, fear index, and more.
                    </p>
                    <a href="/setting?loc=2" className="subscription-btn">
                        Upgrade Plan
                    </a>
                </div>
             </div>
        </div>
    );
  }

  return (
    <div className="stock-dashboard-wrapper mt-4 px-3">
      {showMarketSentimentAndBuzz && (
        <>
          {/* Sentiment Analysis Card */}
          <div className="border-0 shadow-sm rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h5 className="mb-0 fw-normal">Market Sentiment Analysis <TutorialPopup content={t("market-sentiment-analysis-help")} /></h5>
              <div className="sa custom-tabs-container p-1 rounded-3">
                <ul className="nav nav-pills">
                  {['24h', '7d', '30d', '90d'].map(t => (
                    <li className="nav-item" key={t}>
                      <button
                        className={`nav-link py-1 px-3 ${t === selectedRange ? 'active bg-white text-dark' : 'text-muted'}`}
                        onClick={() => setSelectedRange(t)}
                      >
                        {t}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {loading ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : averages ? (
                <>
                    <StockGauge value={averages.sentiment} label="Sentiment" size="large" min={-1} max={1} />

                    {/* Mini Gauges Row */}
                    <div className="row g-3 row-cols-2 row-cols-md-3 row-cols-lg-5 mt-4">
                    {metrics.map((m, i) => (
                        <div className="col" key={i}>
                        <div className="border-0 guage-chart-div bg-opacity-25 rounded-4 border h-100 shadow-none">
                            <StockGauge value={m.value} label={m.label} min={m.min} max={m.max} reverseColors={m.reverseColors} decimals={m.decimals} />
                        </div>
                        </div>
                    ))}
                    </div>
                </>
            ) : (
                <div className="text-center py-5 text-muted">No sentiment data available for this range.</div>
            )}
          </div>

          {/* Media Buzz Activity Card */}
          <div className="border-0 shadow-sm rounded-4 p-4">
            <h5 className="mb-5 fw-normal">Media Buzz Activity</h5>
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={buzzData}>
                  <CartesianGrid vertical={false} strokeDasharray="0" opacity={0.2} />
                  <XAxis dataKey="name" hide />
                  <YAxis axisLine={false} tickLine={false} fontSize={10} tick={{fill: '#8e8e93'}} />
                  <Tooltip 
                      contentStyle={{borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}
                      cursor={{fill: 'transparent'}}
                  />
                  <Bar
                    dataKey="value"
                    fill="#5856d6"
                    radius={[4, 4, 0, 0]}
                    barSize={8}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="d-flex justify-content-between mt-2 text-muted extra-small pt-2 border-top">
              <span>{selectedRange === '24h' ? '24 Hours Ago' : (selectedRange === '7d' ? '7 Days Ago' : (selectedRange === '30d' ? '30 Days Ago' : '90 Days Ago'))}</span>
              <span>Today</span>
            </div>
          </div>
        </>
      )}
      <div className={`border-0 shadow-sm rounded-4 p-4 ${showMarketSentimentAndBuzz ? 'mt-4' : ''}`}>
        <h5 className="mb-5 fw-normal">Assessment </h5>
        
        <div className="mt-2 pt-2 border-top">
          {loading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ReactMarkdown>
              {cleanAssessment}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
};

export default connect(null, { getSentimentAnalysis })(SentimentAnalysis);
